import React, { ReactElement, useState } from 'react';

import { Warning as WarningIcon } from 'src/svg/warning';
import { ErrorExclamation as ErrorIcon } from 'src/svg/error-exclamation';
import { Tip as TipIcon } from 'src/svg/tip';
import { Close } from 'src/svg/close';
import { ThickCheckmark as SuccessIcon } from 'src/svg/thick-checkmark';

import { CloseButton, Container, Content, Controls, IconContainer } from './inline-banner.styles';
import { VisuallyHidden } from '../visually-hidden';

type InlineBannerProps = {
  title?: string;
  description?: string;
  severity?: 'error' | 'info' | 'success' | 'tip' | 'warning';
  cta?: ReactElement;
  padding?: string;
  fontSize?: string;
  hideIcon?: boolean;
  className?: string;
  onClose?: () => void;
};

export function InlineBanner({
  title,
  description,
  severity = 'warning',
  cta,
  padding = '16px',
  fontSize = '13px',
  hideIcon = false,
  className,
  onClose,
}: InlineBannerProps): JSX.Element | null {
  const iconMap = {
    error: <ErrorIcon />,
    tip: <TipIcon />,
    info: <WarningIcon fill='currentColor' />,
    warning: <WarningIcon />,
    success: <SuccessIcon fill='currentColor' />,
  };

  const [isOpen, setIsOpen] = useState(true);

  const handleCloseClick = (): void => {
    setIsOpen(false);
    onClose?.();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Container className={className} $severity={severity} $padding={padding} $fontSize={fontSize}>
      {!hideIcon && <IconContainer>{iconMap[severity]}</IconContainer>}

      <Content $hideIcon={hideIcon}>
        {title && <strong>{title}</strong>}
        {/* eslint-disable-next-line react/no-danger */}
        {description && <div dangerouslySetInnerHTML={{ __html: description }} />}
      </Content>

      {(cta || onClose) && (
        <Controls>
          {cta}

          {onClose && (
            <CloseButton onClick={handleCloseClick} hasCta={!!cta}>
              <VisuallyHidden>Close</VisuallyHidden>
              <Close />
            </CloseButton>
          )}
        </Controls>
      )}
    </Container>
  );
}
